import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, downloadFile } from "../lib/api";
import { Section, LevelBadge, PageSkeleton } from "../components/fields";
import AiSummarySection from "../components/AiSummarySection";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { ArrowLeft, FileDown, FileSpreadsheet, BookOpen, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

const TIP = {
  contentStyle: { backgroundColor: "#0F172A", border: "none", borderRadius: 8, color: "#F8FAFC", fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.25)" },
  labelStyle: { color: "#94A3B8", fontSize: 11 },
  itemStyle: { color: "#F8FAFC" },
  cursor: { stroke: "#CBD5E1", strokeDasharray: "3 3" },
};
const AXIS = { axisLine: false, tickLine: false };

export default function StudentDashboard() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const navigate = useNavigate();

  useEffect(() => { api.get(`/students/${id}/dashboard`).then((r) => setD(r.data)); }, [id]);

  if (!d) return <PageSkeleton />;

  const behaviorData = Object.entries(d.behavior_counts).map(([name, count]) => ({ name, count }));
  const exportF = async (type) => {
    try {
      const params = new URLSearchParams();
      if (range.start) params.set("start", range.start);
      if (range.end) params.set("end", range.end);
      const qs = params.toString() ? `?${params.toString()}` : "";
      await downloadFile(`/export/student/${id}/${type}${qs}`, `support_record.${type === "csv" ? "csv" : "pdf"}`);
      toast.success(`${type.toUpperCase()} export downloaded${range.start || range.end ? ` (${range.start || "start"} → ${range.end || "today"})` : ""}`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button data-testid="dash-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-2"><ArrowLeft size={16} /> Back</button>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{d.student.first_name} {d.student.last_name}</h1>
          <p className="text-sm text-slate-500">Grade {d.student.grade} · {d.today.documented}/{d.today.total} blocks documented today</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2 flex-wrap">
            <button data-testid="view-snapshot-btn" onClick={() => navigate(`/students/${id}`)} className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-4 py-2.5 rounded-full transition-colors duration-150"><BookOpen size={14} /> Snapshot</button>
            <button data-testid="export-pdf-btn" onClick={() => exportF("pdf")} className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-800 px-4 py-2.5 rounded-full transition-colors duration-150"><FileDown size={14} /> PDF</button>
            <button data-testid="export-csv-btn" onClick={() => exportF("csv")} className="flex items-center gap-1.5 text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 px-4 py-2.5 rounded-full transition-colors duration-150"><FileSpreadsheet size={14} /> CSV</button>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <span className="font-bold uppercase tracking-wide text-[10px]">Export range</span>
            <input data-testid="export-start-input" type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="rounded-md border border-slate-200 px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            <span>→</span>
            <input data-testid="export-end-input" type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="rounded-md border border-slate-200 px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600" />
          </div>
        </div>
      </div>

      {d.open_flags.length > 0 && (
        <div data-testid="open-flags-banner" className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1.5">
          {d.open_flags.map((f) => (
            <p key={f.id} className="text-sm text-amber-900 flex items-center gap-2"><LevelBadge level={f.level} /> {f.category} · opened {f.created_at.slice(0, 10)} — awaiting resolution</p>
          ))}
        </div>
      )}

      <Section title="Today's timeline">
        <ul className="divide-y divide-slate-100">
          {d.today.blocks.map((b) => (
            <li key={b.block_id} className="py-2.5 flex items-center gap-3">
              {b.documented ? <CheckCircle2 size={18} className="text-emerald-600 shrink-0" /> : <Circle size={18} className="text-slate-300 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">{b.name} <span className="text-slate-400 font-normal text-xs">{b.start}–{b.end}</span></p>
                {b.entry ? (
                  <p className="text-xs text-slate-500 truncate">{b.entry.prompting_level} prompts · {b.entry.engagement} · {b.entry.work_completion}% completion{b.entry.note ? ` · ${b.entry.note}` : ""}</p>
                ) : (
                  <p className="text-xs text-slate-400">Not yet documented</p>
                )}
              </div>
            </li>
          ))}
          {d.today.blocks.length === 0 && <p className="text-sm text-slate-400">No scheduled blocks today.</p>}
        </ul>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Independence & prompting trend (lower prompting = more independent)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.trends} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(v) => v.slice(5)} {...AXIS} />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} {...AXIS} />
              <Tooltip {...TIP} />
              <Line type="monotone" dataKey="prompting" stroke="#F59E0B" strokeWidth={2.5} dot={false} name="Prompting (0=independent)" />
              <Line type="monotone" dataKey="independence" stroke="#047857" strokeWidth={2.5} dot={false} name="Independence (3=high)" />
            </LineChart>
          </ResponsiveContainer>
        </Section>
        <Section title="Work completion trend (%)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.trends} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(v) => v.slice(5)} {...AXIS} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} {...AXIS} />
              <Tooltip {...TIP} />
              <Line type="monotone" dataKey="completion" stroke="#0EA5E9" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Behavior patterns (30 days)">
          {behaviorData.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={behaviorData} margin={{ top: 5, right: 10, bottom: 0, left: -25 }}>
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#94A3B8" }} interval={0} {...AXIS} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} {...AXIS} />
                <Tooltip {...TIP} cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="count" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400">No incidents in the last 30 days.</p>}
        </Section>
        <Section title="Accommodations usage vs. plan">
          <div className="space-y-2.5">
            {Object.entries(d.accommodation_usage).map(([acc, count]) => (
              <div key={acc}>
                <div className="flex justify-between text-xs mb-1">
                  <span className={`font-medium ${d.rarely_used_accommodations.includes(acc) ? "text-amber-700" : "text-slate-700"}`}>{acc}{d.rarely_used_accommodations.includes(acc) ? " · rarely documented" : ""}</span>
                  <span className="text-slate-400">{count}×</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${d.rarely_used_accommodations.includes(acc) ? "bg-amber-400" : "bg-emerald-600"}`}
                    style={{ width: `${Math.min(100, (count / Math.max(1, d.recent_entries.length)) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <AiSummarySection studentId={id} />

      <Section title="Recent concerns & incidents">
        <div className="space-y-2.5">
          {d.incidents.length === 0 && <p className="text-sm text-slate-400">None recorded.</p>}
          {d.incidents.map((i) => (
            <div key={i.id} data-testid={`incident-${i.id}`} className="border border-slate-100 rounded-lg p-3.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-800">{i.behavior_type}</span>
                <LevelBadge level={i.alert_level} />
                <span className="text-xs text-slate-400">{i.date} · {i.duration_minutes} min · {i.prompt_used} prompt</span>
                {i.incident_form_required && (
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${i.incident_form_completed ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    Incident form {i.incident_form_completed ? "completed" : "pending"}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 mt-1">{i.note}</p>
              <p className="text-xs text-slate-400 mt-1">{i.alert_category} · follow-up: {i.follow_up_status}</p>
            </div>
          ))}
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Communication log">
          <div className="space-y-2.5">
            {d.comm_logs.length === 0 && <p className="text-sm text-slate-400">None.</p>}
            {d.comm_logs.map((c) => (
              <div key={c.id} className="border border-slate-100 rounded-lg p-3.5">
                <p className="text-sm font-medium text-slate-800">{c.communicated_with} <span className="text-xs text-slate-400 font-normal">· {c.mode} · {c.date}</span></p>
                <p className="text-sm text-slate-600 mt-0.5">{c.summary}</p>
                {c.follow_up && <p className="text-xs text-emerald-700 mt-1">Follow-up: {c.follow_up}</p>}
              </div>
            ))}
          </div>
        </Section>
        <Section title="Supervisor feedback & weekly reviews">
          <div className="space-y-2.5">
            {d.coaching_comments.map((c) => (
              <div key={c.id} className="bg-sky-50 border border-sky-100 rounded-lg p-3.5">
                <p className="text-xs font-bold text-sky-800 uppercase tracking-wide">Coaching · {c.supervisor_name} · {c.date}</p>
                <p className="text-sm text-slate-700 mt-1">{c.comment}</p>
              </div>
            ))}
            {d.weekly_reviews.map((r) => (
              <div key={r.id} className="border border-slate-100 rounded-lg p-3.5">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Weekly review · week of {r.week_start}</p>
                <p className="text-sm text-slate-700 mt-1">{r.accomplishments}</p>
                <p className="text-xs text-slate-500 mt-1">Next: {r.improvements}</p>
              </div>
            ))}
            {d.coaching_comments.length === 0 && d.weekly_reviews.length === 0 && <p className="text-sm text-slate-400">None yet.</p>}
          </div>
        </Section>
      </div>

      <Section title="Recent aide notes">
        <div className="space-y-1.5">
          {d.recent_entries.filter((e) => e.note).slice(0, 12).map((e) => (
            <p key={e.id} className="text-sm text-slate-600 border-b border-slate-50 pb-1.5"><span className="text-xs text-slate-400 font-mono">{e.date}</span> · <span className="font-medium text-slate-700">{e.block_name}</span> — {e.note}</p>
          ))}
        </div>
      </Section>
    </div>
  );
}
